import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

function SearchBox() {
  const [query, setQuery] = useState("");
  const [maisBaratos, setMaisBaratos] = useState([]);
  const [melhorAvaliados, setMelhorAvaliados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  const loadingMessages = [
    "Buscando as melhores ofertas...",
    "Comparando preços entre marketplaces...",
    "Verificando avaliações de usuários...",
    "Procurando cupons...",
    "Analisando os resultados..."
  ];

  // Alterna mensagens de loading a cada 2s
  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setLoadingMessageIndex((i) => (i + 1) % loadingMessages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    const script1 = document.createElement("script");
    script1.src = "https://www.googletagmanager.com/gtag/js?id=G-31FPDE4FJR";
    script1.async = true;
    document.head.appendChild(script1);

    const script2 = document.createElement("script");
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', 'G-31FPDE4FJR');
    `;
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setMaisBaratos([]);
    setMelhorAvaliados([]);
    setError("");

    try {
      const res = await fetch(
        "https://55jtm2cq7utnae4v2gsx4wr72q0zhwax.lambda-url.sa-east-1.on.aws/",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query }),
        }
      );

      if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
      const data = await res.json();
      const parsed = data.body ? JSON.parse(data.body) : data;

      if (!parsed.resultados || parsed.resultados.length === 0) {
        setError("Nenhum resultado encontrado.");
        return;
      }

      const marketplaces = ["Amazon", "Mercado Livre"];
      const maisBaratosCalc = [];
      const melhorAvaliadosCalc = [];

      marketplaces.forEach((mp) => {
        const produtosMP = parsed.resultados.filter((p) => p.marketplace === mp);
        maisBaratosCalc.push(...produtosMP.sort((a, b) => a.price - b.price).slice(0, 2));
        melhorAvaliadosCalc.push(...produtosMP.sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 2));
      });

      setMaisBaratos(maisBaratosCalc);
      setMelhorAvaliados(melhorAvaliadosCalc);
    } catch (err) {
      console.error("❌ Erro no fetch:", err);
      setError("Erro ao buscar os dados.");
    } finally {
      setLoading(false);
    }
  };

  const handleAffiliateClick = (productName, affiliateLink) => {
    if (window.gtag) {
      window.gtag("event", "click_afiliado", {
        event_category: "Afiliado",
        event_label: productName,
        value: 1,
      });
    }
    window.open(affiliateLink, "_blank");
  };

  const renderCards = (lista) =>
    lista.map((item, i) => (
      <motion.div
        key={i}
        className="bg-white rounded-xl shadow-md p-5 text-center hover:shadow-lg transition"
        whileHover={{ scale: 1.03 }}
      >
        <motion.img
          src={item.thumbnail}
          alt={item.product_name}
          className="w-32 h-32 object-contain mx-auto"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.1 }}
        />
        <h3 className="text-lg font-semibold mt-2">{item.product_name}</h3>
        <p className="text-green-600 text-2xl font-bold mt-1">
          R$ {item.price.toFixed(2)}
        </p>
        <p className="text-sm text-gray-500">
          {item.marketplace} • ⭐ {item.rating || "N/A"}
        </p>
        <motion.button
          onClick={() => handleAffiliateClick(item.product_name, item.affiliate_link)}
          className="inline-block mt-3 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          whileHover={{ scale: 1.05 }}
        >
          Ver na loja
        </motion.button>
      </motion.div>
    ));

  return (
    <div className="flex flex-col items-center w-full max-w-5xl mx-auto relative">
      {/* Campo de busca */}
      <div className="flex w-full bg-white rounded-xl shadow-md overflow-hidden">
        <input
          type="text"
          placeholder="Busque um produto (ex: teclado, airfryer, tênis...)"
          className="flex-1 px-4 py-3 text-gray-700 focus:outline-none"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <button
          onClick={handleSearch}
          disabled={loading}
          className="bg-blue-600 text-white px-5 py-3 font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <motion.div
                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 2 }}
              />
              Buscando...
            </>
          ) : (
            "Buscar"
          )}
        </button>
      </div>

      {/* Erro */}
      <AnimatePresence>
        {error && (
          <motion.p
            className="text-red-500 mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Resultados */}
      <AnimatePresence>
        {!loading && (maisBaratos.length > 0 || melhorAvaliados.length > 0) && (
          <motion.div
            className="w-full mt-8 space-y-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {maisBaratos.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 text-green-600">💸 Mais Baratos</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {renderCards(maisBaratos)}
                </div>
              </div>
            )}
            {melhorAvaliados.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 text-yellow-600">⭐ Melhor Avaliados</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {renderCards(melhorAvaliados)}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🔹 Modal de Loading */}
      <AnimatePresence>
        {loading && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-sm w-full flex flex-col items-center"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <motion.div
                className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1 }}
              />
              <AnimatePresence mode="wait">
                <motion.p
                  key={loadingMessageIndex}
                  className="text-gray-700 font-medium"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                >
                  {loadingMessages[loadingMessageIndex]}
                </motion.p>
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SearchBox;
